import { openDB } from 'idb';
import { Logger } from "./Logger.js";
import { selected_devices } from './stores.js';

import { get } from "svelte/store";

const DB_NAME = 'pl-user-info';
const DB_VERSION = 1;
const STORE = 'prefs';
const KEY = 'start-panel';

let dbPromise;

function getDB() {
    if (!dbPromise) {
        dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                db.createObjectStore(STORE);
            },
        });
    }

    return dbPromise;
}

/*
 start-panel -- a snapshot of the desired device state:
   {name: "", devices: {camera: {label, muted}, microphone: {label}, speaker: {label}, stream: {label}}}

 Older payloads stored {selected_labels: {camera: "label"|"Mute", ...}} where a muted camera's
 label was REPLACED by the literal "Mute" (so the device itself was forgotten); load() migrates
 those in memory and the next save writes the new shape.
*/
export const UserPrefs = {

    prefs: null,

    async writeNameAndSelectedDevices(name, { keep_stored_stream = false } = {}){

        const _selected_devices = get(selected_devices);
        const devices = {};

        // -- re-read rather than trusting the in-memory snapshot: it dates from page load, and
        // -- an eng tab on this profile may have saved its stream monitor since -- the very
        // -- pref this flag preserves
        const stored_stream = keep_stored_stream
            ? (await UserPrefs.load())?.devices?.stream?.label ?? null
            : null;

        for (const type of ["camera", "microphone", "speaker", "stream"]){

            // -- a derived stream selection (see OutputDevices.streamIndependent) is just a copy
            // -- of the speaker; persisting it would stomp the stream monitor an eng session
            // -- chose on this same browser profile. Keep whatever is stored instead.
            if (type === "stream" && keep_stored_stream){
                if (stored_stream)
                    devices.stream = { label: stored_stream };
                continue;
            }

            const selection = _selected_devices[type];
            if (!selection)
                continue;

            devices[type] = { label: selection.label ?? null };

            // -- only the camera's mute is desired state worth reloading: a muted camera loads
            // -- closed (light off). The mic always opens (it feeds the local monitor) and the
            // -- stream monitor has its own role-based default (OutputDevices).
            if (type === "camera")
                devices[type].muted = selection.muted ? get(selection.muted) : false;
        }

        await UserPrefs.save(name ?? "", devices);
    },

    // -- a mid-session mic change (best audio): only the mic, the rest stays as the last Join wrote it
    async writeMicrophone(label){
        const stored = await UserPrefs.load();
        if (!stored)
            return;
        await UserPrefs.save(stored.name ?? "", { ...(stored.devices ?? {}), microphone: { label } });
    },

    async save(name, devices) {
        const db = await getDB();
        const p = { name, devices };
        this.prefs = p;
        await db.put(STORE, p, KEY);
    },

    async load() {
        try {
            const db = await getDB();
            this.prefs = this._migrate(await db.get(STORE, KEY));
            return this.prefs;
        } catch (e) {
            Logger.log("User Prefs", "warn", "Failed to load user prefs", {error: e.message});
            this.prefs = null;
            return this.prefs;
        }
    },

    // -- legacy {selected_labels} payloads: values were plain label strings, with the camera's
    // -- label overwritten by "Mute" when saved muted -- that camera migrates to
    // -- {label: null, muted: true} (the device name is simply gone in the old data)
    _migrate(prefs){

        if (!prefs || prefs.devices || !prefs.selected_labels)
            return prefs ?? null;

        const devices = {};

        for (const [type, value] of Object.entries(prefs.selected_labels)){

            const label = typeof value === "string" ? value : value?.label ?? null;

            if (type === "camera" && label === "Mute")
                devices.camera = { label: null, muted: true };
            else if (label)
                devices[type] = { label: label };
        }

        return { name: prefs.name ?? "", devices: devices };
    },
};
